import { useState } from 'react';

const contactFormUrl = 'https://readdy.ai/api/form/d9qsnaknlsngrm4lm5bg';

export default function ContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) return;

    const form = e.currentTarget;
    const formDataObj = new FormData(form);

    const hpField = formDataObj.get('company_alt');
    if (hpField && String(hpField).trim() !== '') {
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const payload = new URLSearchParams();
      payload.append('name', formData.name.trim());
      payload.append('email', formData.email.trim());
      payload.append('subject', formData.subject.trim());
      payload.append('message', formData.message.trim());

      const response = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* use raw responseText */ }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setErrorMsg(serverMsg);
        setStatus('error');
      }
    } catch {
      setErrorMsg('网络错误，请检查连接后重试');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="p-8 rounded-xl bg-background-900 border border-background-800/60 text-center flex flex-col items-center justify-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-500/10 mb-4">
          <i className="ri-check-double-line text-3xl text-primary-400"></i>
        </div>
        <h3 className="text-xl font-bold text-foreground-200 mb-2">消息已发送</h3>
        <p className="text-foreground-500 text-sm">感谢您的来信，我们将在 48 小时内回复。</p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-5 px-5 py-2 rounded-lg bg-background-800 text-foreground-400 text-sm hover:text-foreground-200 transition-colors duration-200"
        >
          发送新消息
        </button>
      </div>
    );
  }

  return (
    <form
      id="contact-form"
      data-readdy-form="true"
      onSubmit={handleSubmit}
      className="p-6 rounded-xl bg-background-900 border border-background-800/60 space-y-4"
    >
      <h3 className="text-lg font-bold text-foreground-200 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
        发送消息
      </h3>

      <div>
        <label htmlFor="contact-name" className="block text-xs text-foreground-500 mb-1.5">姓名 *</label>
        <input
          id="contact-name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full px-3.5 py-2.5 rounded-lg bg-background-800 border border-background-700/60 text-foreground-200 text-sm placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-all duration-200"
          placeholder="您的姓名"
        />
      </div>

      <div>
        <label htmlFor="contact-email" className="block text-xs text-foreground-500 mb-1.5">邮箱 *</label>
        <input
          id="contact-email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="w-full px-3.5 py-2.5 rounded-lg bg-background-800 border border-background-700/60 text-foreground-200 text-sm placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-all duration-200"
          placeholder="your@email.com"
        />
      </div>

      <div>
        <label htmlFor="contact-subject" className="block text-xs text-foreground-500 mb-1.5">主题</label>
        <input
          id="contact-subject"
          type="text"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          className="w-full px-3.5 py-2.5 rounded-lg bg-background-800 border border-background-700/60 text-foreground-200 text-sm placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-all duration-200"
          placeholder="消息主题"
        />
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-xs text-foreground-500 mb-1.5">消息内容 *</label>
        <textarea
          id="contact-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          maxLength={500}
          rows={4}
          className="w-full px-3.5 py-2.5 rounded-lg bg-background-800 border border-background-700/60 text-foreground-200 text-sm placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/30 transition-all duration-200 resize-none"
          placeholder="请输入您的消息内容（不超过500字）"
        ></textarea>
        <p className="text-xs text-foreground-600 mt-1 text-right">{formData.message.length}/500</p>
      </div>

      <input
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="contact-form-hp"
      />

      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-200 disabled:opacity-60 whitespace-nowrap"
      >
        {status === 'loading' ? (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-loader-4-line animate-spin"></i> 发送中
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <i className="ri-send-plane-line"></i> 发送消息
          </span>
        )}
      </button>

      {status === 'error' && (
        <p className="text-sm text-primary-400 text-center">{errorMsg}</p>
      )}
    </form>
  );
}